// localStorage trip store (key tp_trips_v3) — LEGACY persistence path kept
// for the one-time migration in App.js (local trips are uploaded to the
// server on first authed load), plus shared helpers that are still active:
// destSwatch (stable gradient per destination), daysUntil, and the default
// packing list storage.
const KEY = 'tp_trips_v3';

export const loadTrips = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
};

const persist = (trips) => localStorage.setItem(KEY, JSON.stringify(trips));

export const createTrip = (data) => {
  const trips = loadTrips();
  const trip = {
    id: `tp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    createdAt: Date.now(),
    status: 'planning',
    bookings: {},
    ...data,
  };
  persist([trip, ...trips]);
  return trip;
};

export const updateTrip = (id, updates) => {
  const trips = loadTrips().map(t => t.id === id ? { ...t, ...updates } : t);
  persist(trips);
};

export const deleteTrip = (id) => persist(loadTrips().filter(t => t.id !== id));

// Stable gradient from destination string
const SWATCHES = [
  'linear-gradient(135deg, #E8C9A0 0%, #D08B5C 60%, #8B4A2A 100%)',
  'linear-gradient(135deg, #E2A0A0 0%, #B85450 50%, #5C2424 100%)',
  'linear-gradient(135deg, #B8C4D0 0%, #5C7080 50%, #2A3540 100%)',
  'linear-gradient(135deg, #A8C8D8 0%, #406888 100%)',
  'linear-gradient(135deg, #B8A8D0 0%, #503870 100%)',
  'linear-gradient(135deg, #C9D0B8 0%, #607048 100%)',
  'linear-gradient(135deg, #D8C4A0 0%, #A07840 100%)',
];
export const destSwatch = (dest = '') => {
  let h = 0;
  for (const c of dest) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
  return SWATCHES[h % SWATCHES.length];
};

// Days until a date string like "2026-06-12" or "Jun 12"
export const daysUntil = (dateStr) => {
  try {
    const months = { Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12 };
    let d;
    if (dateStr.includes('-')) {
      d = new Date(dateStr);
    } else {
      const [mon, day] = dateStr.split(' ');
      const m = months[mon];
      if (!m) return null;
      d = new Date(`${new Date().getFullYear()}-${String(m).padStart(2,'0')}-${String(day).padStart(2,'0')}`);
    }
    const diff = Math.ceil((d - Date.now()) / 86400000);
    return diff > 0 ? diff : null;
  } catch { return null; }
};

// Format date range for display
export const fmtRange = (start, end) => {
  if (!start) return '';
  if (!end) return start;
  return `${start} – ${end}`;
};

// ISO "YYYY-MM-DD" -> "DD/MM/YY" for user-facing display (not American M/D/Y).
export const fmtDMY = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y.slice(2)}`;
};

// Trips ordered by dateStart ascending; undated trips sort last.
export const sortByDateStart = (trips) => {
  return [...trips].sort((a, b) => {
    if (!a.dateStart && !b.dateStart) return 0;
    if (!a.dateStart) return 1;
    if (!b.dateStart) return -1;
    return a.dateStart < b.dateStart ? -1 : a.dateStart > b.dateStart ? 1 : 0;
  });
};

// ── Packing list ──────────────────────────────────────────────────────────────

const PACKING_KEY = 'tp_packing_default';

export const DEFAULT_PACKING = [
  { cat: 'Documents', items: ['Passport', 'Visa printout', 'Travel insurance docs', 'Flight bookings printout', 'Hotel bookings printout'] },
  { cat: 'Clothing', items: ['T-shirts', 'Pants/shorts', 'Underwear', 'Socks', 'Pyjamas', 'Light jacket/cardigan', 'Walking shoes', 'Sandals'] },
  { cat: 'Electronics', items: ['Phone charger', 'Power bank', 'Universal adapter', 'Earphones/AirPods', 'Camera'] },
  { cat: 'Toiletries', items: ['Toothbrush & toothpaste', 'Shampoo & conditioner', 'Body wash', 'Deodorant', 'Sunscreen', 'Moisturiser'] },
  { cat: 'Health', items: ['Prescription medications', 'Paracetamol/Panadol', 'Plasters', 'Insect repellent', 'Hand sanitiser', 'Face masks'] },
  { cat: 'Misc', items: ['Luggage lock', 'Reusable tote bag', 'Snacks for flight', 'Travel pillow', 'Eye mask & earplugs'] },
];

export const loadDefaultPacking = () => {
  try {
    const raw = localStorage.getItem(PACKING_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_PACKING;
  } catch { return DEFAULT_PACKING; }
};

export const saveDefaultPacking = (cats) => {
  localStorage.setItem(PACKING_KEY, JSON.stringify(cats));
};

export const initTripPacking = (trip) => {
  if (trip?.packingList?.length) return trip.packingList;
  const cats = loadDefaultPacking();
  const list = [];
  for (const cat of cats) {
    for (const item of cat.items) {
      list.push({
        id: `pk_${Math.random().toString(36).slice(2, 9)}`,
        cat: cat.cat,
        text: item,
        checked: false,
      });
    }
  }
  return list;
};
